import { remove } from 'immutable';
import * as React from 'react';
import { useState } from 'react';
import {
    Button,
    Col,
    Form,
    FormGroup,
    Input,
    Label,
    Row
} from 'reactstrap';

interface UserDetails {
    secretQuestion1: string;
    secretQuestion2: string;
    secretQuestion3: string;
    secretAnswer1: string;
    secretAnswer2: string;
    secretAnswer3: string;
}

interface ManageSecretQuestionsProps {
    details: UserDetails;
    editMode: boolean;
    isFirstLogin: boolean;
    onSubmit: Function;
    onCancel?: Function;
    questionLength: number;
    answerLength: number
}

const ManageSecretQuestions: React.FC<ManageSecretQuestionsProps> = (props) => {
    const [user, setUser] = useState<UserDetails>({ ...props.details });
    const [isEditMode, setIsEditMode] = useState<boolean>(props.editMode);
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [validations, setValidations] = useState({
        isQuestion1Valid: true,
        isQuestion2Valid: true,
        isQuestion3Valid: true,
        isAnswer1Valid: true,
        isAnswer2Valid: true,
        isAnswer3Valid: true
    });

    const isQuestionValid = (question: string) => {
        var result = false;
        if (question && question.trim() !== '') {
            if (question.length >= props.questionLength) {
                result = true;
            }
        }
        return result;
    };

    const isAnswerValid = (answer: string) => {
        var result = false;
        if (answer && answer.trim() !== '') {
            if (answer.length >= props.answerLength) {
                result = true;
            }
        }
        return result;
    }

    const validateField = (field: any, value: any) => {
        const isValid =
            field.includes('Question') ? isQuestionValid(value) :
                field.includes('Answer') ? isAnswerValid(value) : true;
        return isValid;
    }

    const validate = () => {
        const validationSet = {
            isQuestion1Valid: validateField("secretQuestion1", user.secretQuestion1),
            isQuestion2Valid: validateField("secretQuestion2", user.secretQuestion2),
            isQuestion3Valid: validateField("secretQuestion3", user.secretQuestion3),
            isAnswer1Valid: validateField("secretAnswer1", user.secretAnswer1),
            isAnswer2Valid: validateField("secretAnswer2", user.secretAnswer2),
            isAnswer3Valid: validateField("secretAnswer3", user.secretAnswer3)
        }
        setValidations(validationSet);
        return Object.values(validationSet).every((value: any) => value === true);
    }

    const onSubmit = (event: any) => {
        event.preventDefault();
        if (validate()) {
            props.onSubmit(user, event);
        }
    }

    const handleChange = (event: any) => {
        const { name, value } = event.target;

        setUser((prevUser) => ({ ...prevUser, [name]: value }));
    };

    const handleEdit = (event: any) => {
        event.preventDefault();
        setIsEditMode(!isEditMode);
    };

    const handleCancel = (event: any) => {
        event.preventDefault();
        setUser({ ...props.details });
        setIsEditMode(false);
        setValidations({
            isQuestion1Valid: true,
            isQuestion2Valid: true,
            isQuestion3Valid: true,
            isAnswer1Valid: true,
            isAnswer2Valid: true,
            isAnswer3Valid: true
        });

        props.onCancel && props.onCancel();
    };

    return (
        <Form onSubmit={onSubmit} className="secretQuestionsForm" >
            {isEditMode &&
                <Row>
                    <Col lg={"12"}>
                        <p className="form-text">Please answer three secret questions and answers only you can answer. In the event that you forget your password, you will be asked to answer these questions to verify your identity before you can receive a new password.</p>
                        <p className="form-text">Secret Questions and Answers must have a minimum of 5 characters.</p>
                    </Col>
                </Row>
            }
            <Row>
                <Col lg={props.isFirstLogin ? "6" : "3"}>
                    <FormGroup>
                        <Label for="secretQuestion1" className="fw-bold">SECRET QUESTION 1</Label>
                        <p hidden={isEditMode}>{!props.details.secretQuestion1 ? <span>{'-'}</span> : props.details.secretQuestion1}</p>
                        <div hidden={!isEditMode}>
                            <Input type="text" name="secretQuestion1" id="secretQuestion1" placeholder="Question 1" invalid={(!validations.isQuestion1Valid) ? true : undefined}
                                onChange={handleChange} value={!user.secretQuestion1 ? '' : user.secretQuestion1} />
                            <small id="secretQuestion1Required" className="form-text text-muted">Required</small>
                            <div className="invalid-feedback">
                                {user.secretQuestion1 && user.secretQuestion1.trim() !== '' ?
                                    <span>Question length must be at least {props.questionLength} characters</span>
                                    : <span>Please provide a valid question</span>}
                            </div>
                        </div>
                    </FormGroup>
                </Col>
                <Col lg={props.isFirstLogin ? "6" : "3"}>
                    <FormGroup>
                        <Label for="secretAnswer1" className="fw-bold">SECRET ANSWER 1</Label>
                        <p hidden={isEditMode}>{!props.details.secretAnswer1 ? <span>{'-'}</span> : props.details.secretAnswer1}</p>
                        <div hidden={!isEditMode}>
                            <Input type="text" name="secretAnswer1" id="secretAnswer1" placeholder="Answer 1" invalid={(!validations.isAnswer1Valid) ? true : undefined}
                                onChange={handleChange} value={!user.secretAnswer1 ? '' : user.secretAnswer1} />
                            <small id="secretAnswer1Required" className="form-text text-muted">Required</small>
                            <div className="invalid-feedback">
                                {user.secretAnswer1 && user.secretAnswer1.trim() !== '' ?
                                    <span>Answer length must be at least {props.questionLength} characters</span>
                                    : <span>Please provide a valid answer</span>}
                            </div>
                        </div>
                    </FormGroup>
                </Col>
            </Row>
            <Row>
                <Col lg={props.isFirstLogin ? "6" : "3"}>
                    <FormGroup>
                        <Label for="secretQuestion2" className="fw-bold">SECRET QUESTION 2</Label>
                        <p hidden={isEditMode}>{!props.details.secretQuestion2 ? <span>{'-'}</span> : props.details.secretQuestion2}</p>
                        <div hidden={!isEditMode}>
                            <Input type="text" name="secretQuestion2" id="secretQuestion2" placeholder="Question 2" invalid={(!validations.isQuestion2Valid) ? true : undefined}
                                onChange={handleChange} value={!user.secretQuestion2 ? '' : user.secretQuestion2} />
                            <small id="secretQuestion2Required" className="form-text text-muted">Required</small>
                            <div className="invalid-feedback">
                                {user.secretQuestion2 && user.secretQuestion2.trim() !== '' ?
                                    <span>Question length must be at least {props.questionLength} characters</span>
                                    : <span>Please provide a valid question</span>}
                            </div>
                        </div>
                    </FormGroup>
                </Col>
                <Col lg={props.isFirstLogin ? "6" : "3"}>
                    <FormGroup>
                        <Label for="secretAnswer2" className="fw-bold">SECRET ANSWER 2</Label>
                        <p hidden={isEditMode}>{!props.details.secretAnswer2 ? <span>{'-'}</span> : props.details.secretAnswer2}</p>
                        <div hidden={!isEditMode}>
                            <Input type="text" name="secretAnswer2" id="secretAnswer2" placeholder="Answer 2" invalid={(!validations.isAnswer2Valid) ? true : undefined}
                                onChange={handleChange} value={!user.secretAnswer2 ? '' : user.secretAnswer2} />
                            <small id="secretAnswer2Required" className="form-text text-muted">Required</small>
                            <div className="invalid-feedback">
                                {user.secretAnswer2 && user.secretAnswer2.trim() !== '' ?
                                    <span>Answer length must be at least {props.answerLength} characters</span>
                                    : <span>Please provide a valid answer</span>}
                            </div>
                        </div>
                    </FormGroup>
                </Col>
            </Row>
            <Row>
                <Col lg={props.isFirstLogin ? "6" : "3"}>
                    <FormGroup>
                        <Label for="secretQuestion3" className="fw-bold">SECRET QUESTION 3</Label>
                        <p hidden={isEditMode}>{!props.details.secretQuestion3 ? <span>{'-'}</span> : props.details.secretQuestion3}</p>
                        <div hidden={!isEditMode}>
                            <Input type="text" name="secretQuestion3" id="secretQuestion3" placeholder="Question 3" invalid={(!validations.isQuestion3Valid) ? true : undefined}
                                onChange={handleChange} value={!user.secretQuestion3 ? '' : user.secretQuestion3} />
                            <small id="secretQuestion3Required" className="form-text text-muted">Required</small>
                            <div className="invalid-feedback">
                                {user.secretQuestion3 && user.secretQuestion3.trim() !== '' ?
                                    <span>Question length must be at least {props.answerLength} characters</span>
                                    : <span>Please provide a valid question</span>}
                            </div>
                        </div>
                    </FormGroup>
                </Col>
                <Col lg={props.isFirstLogin ? "6" : "3"}>
                    <FormGroup>
                        <Label for="secretAnswer3" className="fw-bold">SECRET ANSWER 3</Label>
                        <p hidden={isEditMode}>{!props.details.secretAnswer3 ? <span>{'-'}</span> : props.details.secretAnswer3}</p>
                        <div hidden={!isEditMode}>
                            <Input type="text" name="secretAnswer3" id="secretAnswer3" placeholder="Answer 3" invalid={(!validations.isAnswer3Valid) ? true : undefined}
                                onChange={handleChange} value={!user.secretAnswer3 ? '' : user.secretAnswer3} />
                            <small id="secretAnswer3Required" className="form-text text-muted">Required</small>
                            <div className="invalid-feedback">
                                {user.secretAnswer3 && user.secretAnswer3.trim() !== '' ?
                                    <span>Answer length must be at least {props.answerLength} characters</span>
                                    : <span>Please provide a valid answer</span>}
                            </div>
                        </div>
                    </FormGroup>
                </Col>
            </Row>
            <Row>
                <Col lg={props.isFirstLogin ? "6" : "3"}>
                    {isEditMode === true ?
                        <span>
                            <Button type="submit" color="primary" style={{ marginRight: '10px' }}>Save</Button>
                            <Button onClick={handleCancel}>Cancel</Button>
                        </span>
                        : <span><Button color="primary" onClick={handleEdit} style={{ marginBottom: '20px' }}>Edit</Button></span>}
                </Col>
            </Row>
        </Form>
    );
}
export default ManageSecretQuestions;